import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { EventCountdowns } from "@/components/EventCountdowns";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isSupervisorOrAbove, isTopTier } from "@/lib/permissions";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CalendarBlank, Package, ClipboardText, FileText, Warning, ShieldCheck, ChartBar, Clock, ListChecks,
} from "@phosphor-icons/react";

function Tile({ icon: Icon, label, value, hint, tone = "default", testid }) {
  const tones = {
    default: "border-slate-200 bg-white",
    danger: "border-red-300 bg-red-50",
    warn: "border-amber-300 bg-amber-50",
    ok: "border-emerald-300 bg-emerald-50",
  };
  return (
    <div className={`surface-grid p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="tab-tag text-slate-500">{label}</div>
        <Icon size={18} weight="bold" className="text-slate-700" />
      </div>
      <div className="mt-3 font-display text-3xl font-black tracking-tighter text-slate-900">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  // Drop Requests are a supervisor-and-above concern. Volunteers see
  // only their own upcoming tours + hours, not the ops queue.
  const canSeeDrops = isSupervisorOrAbove(user);
  // Only Staff Chief / Commanding Officer / Super Admin (and original
  // admin) see pending supply-request + low-stock alerts on the
  // dashboard. Everyone else focuses on their own tours and hours.
  const canSeeOpsAlerts = isTopTier(user);
  const [tours, setTours] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [supplyReqs, setSupplyReqs] = useState([]);
  const [myTours, setMyTours] = useState([]);
  const [monthly, setMonthly] = useState(null);
  const [settings, setSettings] = useState(null);
  const [dropPattern, setDropPattern] = useState(null); // {flagged_count, flagged[]}

  useEffect(() => {
    (async () => {
      try {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
        const commonReqs = [
          api.get(`/tours/?start=${start}&end=${end}`),
          api.get("/tours/mine").catch(() => ({ data: [] })),
          api.get("/reports/monthly").catch(() => ({ data: null })),
          api.get("/settings/").catch(() => ({ data: null })),
        ];
        const [t, mt, mo, st] = await Promise.all(commonReqs);
        setTours(t.data); setMyTours(mt.data); setMonthly(mo.data); setSettings(st.data);
        // Supervisor+ also pulls the rolling-30-day drop pattern watch.
        if (canSeeDrops) {
          api.get("/reports/drop-tours")
            .then((r) => setDropPattern(r.data?.pattern_watch || null))
            .catch(() => setDropPattern(null));
        }
        // Only fetch supplies + low stock for top-tier chiefs.
        if (canSeeOpsAlerts) {
          const [ls, sr] = await Promise.all([
            api.get("/inventory/low-stock").catch(() => ({ data: [] })),
            api.get("/supply-requests/").catch(() => ({ data: [] })),
          ]);
          setLowStock(ls.data); setSupplyReqs(sr.data);
        }
      } catch {}
    })();
  }, [canSeeDrops, canSeeOpsAlerts]);

  const openTours = tours.filter((t) => t.status === "open").length;
  const pendingDrops = tours.filter((t) => t.status === "drop_pending").length;
  const pendingSupplies = supplyReqs.filter((s) => s.status === "pending").length;
  const myUpcoming = myTours.filter((t) => new Date(t.end_time) > new Date()).slice(0, 5);
  const myHours = monthly?.rows?.find((r) => r.id === user?.id)?.hours || 0;
  const threshold = settings?.monthly_hours_threshold ?? monthly?.threshold ?? 20;
  const underCount = (monthly?.rows || []).filter((r) => r.role === "volunteer" && r.hours < threshold).length;

  return (
    <AppLayout title={`Welcome, ${user?.name?.split(" ")[0] || ""}`} subtitle="Today's operational snapshot">
      <EventCountdowns />
      <div className={`grid grid-cols-2 ${canSeeOpsAlerts ? "md:grid-cols-4" : canSeeDrops ? "md:grid-cols-2" : "md:grid-cols-1"} gap-4`}>
        <Tile testid="tile-open-tours" icon={CalendarBlank} label="Open Tours (Month)" value={openTours} hint="Ready for pickup" tone={openTours > 0 ? "ok" : "default"} />
        {canSeeDrops && (
          <Tile testid="tile-drops" icon={ListChecks} label="Drop Requests" value={pendingDrops} hint="Awaiting supervisor approval" tone={pendingDrops > 0 ? "warn" : "default"} />
        )}
        {canSeeOpsAlerts && (
          <>
            <Tile testid="tile-supplies" icon={ClipboardText} label="Pending Supplies" value={pendingSupplies} hint="Awaiting decision" tone={pendingSupplies > 0 ? "warn" : "default"} />
            <Tile testid="tile-low-stock" icon={Warning} label="Low Stock Items" value={lowStock.length} hint={`Threshold breached`} tone={lowStock.length > 0 ? "danger" : "default"} />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="rounded-sm border-slate-200 lg:col-span-2" data-testid="card-upcoming">
          <CardHeader className="border-b border-slate-200 py-3">
            <CardTitle className="font-display flex items-center gap-2 text-base"><Clock size={16} weight="bold" /> Your Upcoming Tours</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {myUpcoming.length === 0 ? (
              <div className="p-6 text-sm text-slate-500">No upcoming tours assigned. Pick one up from the <Link className="underline" to="/schedule">Schedule</Link>.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left tab-tag text-slate-500">
                    <th className="px-4 py-2">When</th><th className="px-4 py-2">Unit</th><th className="px-4 py-2">Cert</th><th className="px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {myUpcoming.map((t) => (
                    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 mono">{new Date(t.start_time).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-4 py-2"><Badge className="rounded-sm bg-slate-900 text-white">{t.unit}</Badge></td>
                      <td className="px-4 py-2"><Badge className="rounded-sm bg-blue-600 text-white">{t.required_certification}</Badge></td>
                      <td className="px-4 py-2 mono uppercase text-xs">{t.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-sm border-slate-200" data-testid="card-month-hours">
          <CardHeader className="border-b border-slate-200 py-3">
            <CardTitle className="font-display flex items-center gap-2 text-base"><ChartBar size={16} weight="bold" /> Your Monthly Hours</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="font-display text-5xl font-black tracking-tighter text-slate-900 mono">{myHours.toFixed ? myHours.toFixed(1) : myHours}</div>
            <div className={`mt-2 text-sm ${myHours < threshold ? "text-red-600 font-semibold" : "text-emerald-700"}`}>
              {myHours < threshold ? `Under threshold (${threshold} hrs)` : `Meeting threshold (${threshold} hrs)`}
            </div>
            <div className="mt-4 h-2 bg-slate-100 relative">
              <div className={`absolute left-0 top-0 h-full ${myHours < threshold ? "bg-red-500" : "bg-emerald-600"}`} style={{ width: `${Math.min(100, (myHours / threshold) * 100)}%` }} />
            </div>
            <Link to="/reports" className="mt-4 inline-block text-xs underline text-slate-700">Full report →</Link>
          </CardContent>
        </Card>
      </div>

      {isSupervisorOrAbove(user) && (
        <Card className="rounded-sm border-slate-200 mt-4" data-testid="card-sup-summary">
          <CardHeader className="border-b border-slate-200 py-3">
            <CardTitle className="font-display flex items-center gap-2 text-base"><ShieldCheck size={16} weight="bold" /> Supervisor Snapshot</CardTitle>
          </CardHeader>
          <CardContent className={`p-4 grid grid-cols-1 ${canSeeOpsAlerts ? "md:grid-cols-4" : "md:grid-cols-3"} gap-4 text-sm`}>
            <div><div className="tab-tag text-slate-500">Drop Approvals</div><div className="font-display text-2xl font-bold mono">{pendingDrops}</div></div>
            <div>
              <div className="tab-tag text-slate-500">Drop Pattern Watch</div>
              <div className={`font-display text-2xl font-bold mono ${dropPattern?.flagged_count > 0 ? "text-amber-700" : "text-slate-900"}`} data-testid="dash-pattern-count">
                {dropPattern?.flagged_count ?? 0}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">3+ drops in 30 days · <Link to="/admin/reports" className="underline hover:text-slate-700">details →</Link></div>
            </div>
            {canSeeOpsAlerts && (
              <div><div className="tab-tag text-slate-500">Pending Supply Requests</div><div className="font-display text-2xl font-bold mono">{pendingSupplies}</div></div>
            )}
            <div><div className="tab-tag text-slate-500">Members Under Threshold</div><div className={`font-display text-2xl font-bold mono ${underCount > 0 ? "text-red-600" : ""}`}>{underCount}</div></div>
          </CardContent>
          {dropPattern?.flagged_count > 0 && (
            <div className="px-4 pb-4" data-testid="dash-pattern-chips">
              <div className="text-[10px] uppercase tracking-widest text-amber-800 mb-1.5">Flagged Members</div>
              <div className="flex flex-wrap gap-1.5">
                {dropPattern.flagged.slice(0, 8).map((f) => (
                  <span
                    key={f.volunteer_id}
                    className="inline-flex items-center gap-1.5 border border-amber-300 bg-amber-50 rounded-sm px-2 py-0.5 text-xs"
                    data-testid={`dash-flag-${f.volunteer_id}`}
                  >
                    <span className="font-medium">{f.volunteer_name}</span>
                    <span className="mono text-[10px] text-amber-800">{f.drop_count_30d}x</span>
                  </span>
                ))}
                {dropPattern.flagged.length > 8 && (
                  <span className="text-[10px] text-slate-500 self-center">+{dropPattern.flagged.length - 8} more</span>
                )}
              </div>
            </div>
          )}
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Link to="/schedule" className="surface-grid p-4 hover:bg-slate-50" data-testid="quick-schedule">
          <div className="flex items-center gap-3"><CalendarBlank size={22} weight="bold" /><div><div className="font-display font-bold">Open Schedule</div><div className="text-xs text-slate-500">Pick up or manage tours</div></div></div>
        </Link>
        <Link to="/documents" className="surface-grid p-4 hover:bg-slate-50" data-testid="quick-docs">
          <div className="flex items-center gap-3"><FileText size={22} weight="bold" /><div><div className="font-display font-bold">Upload Documents</div><div className="text-xs text-slate-500">Certifications, CPR, EMT, ID</div></div></div>
        </Link>
      </div>
    </AppLayout>
  );
}
